import {
  DocumentReference,
  isDocRef,
  ISODateTimeWithUTCType,
  TypeGuard,
} from '@principle-theorem/shared';
import { isString } from 'lodash';
import type { IPractice } from '../../../practice/practice';
import type { IOvieHealthAIPerformanceAnalytics } from './ai-performance-analytics';
import type { IOvieHealthAppointmentAnalytics } from './appointment-analytics';
import type { IOvieHealthDateRange } from './analytics-common';
import type { IOvieHealthCallVolumeAnalytics } from './call-volume-analytics';
import type { IOvieHealthOverviewMetrics } from './overview-metrics';
import type { IOvieHealthSentimentAnalytics } from './sentiment-analytics';

export enum OvieHealthAnalyticsAction {
  GetOverview = 'getOverview',
  GetSentiment = 'getSentiment',
  GetCallVolume = 'getCallVolume',
  GetAppointments = 'getAppointments',
  GetAiPerformance = 'getAiPerformance',
}

export function isOvieHealthAnalyticsAction(
  data: unknown
): data is OvieHealthAnalyticsAction {
  return Object.values(OvieHealthAnalyticsAction).includes(
    data as OvieHealthAnalyticsAction
  );
}

export interface IOvieHealthAnalyticsActionPayloadMap {
  getOverview: IOvieHealthOverviewMetrics;
  getSentiment: IOvieHealthSentimentAnalytics;
  getCallVolume: IOvieHealthCallVolumeAnalytics;
  getAppointments: IOvieHealthAppointmentAnalytics;
  getAiPerformance: IOvieHealthAIPerformanceAnalytics;
}

export interface IOvieHealthAnalyticsRequest<
  A extends OvieHealthAnalyticsAction = OvieHealthAnalyticsAction,
> {
  action: A;
  practiceRef: DocumentReference<IPractice>;
  range: IOvieHealthDateRange;
}

export const isOvieHealthAnalyticsRequest =
  TypeGuard.interface<IOvieHealthAnalyticsRequest>({
    action: isOvieHealthAnalyticsAction,
    practiceRef: isDocRef,
    range: TypeGuard.interface<IOvieHealthDateRange>({
      from: isString,
      to: isString,
    }),
  });

export interface IOvieHealthAnalyticsResponse<
  A extends OvieHealthAnalyticsAction = OvieHealthAnalyticsAction,
> {
  action: A;
  range: IOvieHealthDateRange;
  data: IOvieHealthAnalyticsActionPayloadMap[A];
  cacheHit: boolean;
  fetchedAt: ISODateTimeWithUTCType;
}
